import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Typography
} from '@material-ui/core';
import AdditionalFilters from 'components/sidebar/CareConnect/additionalFilters/AdditionalFilters';
import MultiSelectDropdown from '../MultiSelectDropdown';
import { IClinicOptionsList } from 'model/clinic';
import { useLazyQuery, useQuery } from 'react-apollo';
import { GET_ATTENDEES } from 'api/graphql/v2/queries/Attendees';
import { alphabeticalSort } from 'utils/sort';
import './ClientTab.css';
import AutocompleteSearch from '../AutocompleteSearch';
import { CURRENT_CLIENT_TAB_COLUMNS } from 'views/components/careConnectPage/ClientCare/clientTab';
import DataGrid from 'views/containers/datagrid';
import FilterListIcon from '@material-ui/icons/FilterList';
import { GET_WAITLIST_INITIAL_LOAD_DATA } from 'api/graphql/queries/careplan';
import {
  GET_CARECONNECT_CLIENTS,
  GET_SHIFT_DROPDOWN_OPTIONS
} from 'api/graphql/queries/careconnect';
import { IClientTabRow } from 'model/v2/common';
import FilterChips from 'components/sidebar/CareConnect/FilterChips/FilterChips';
import { RECOMMENDED_HOURS_RANGE } from 'utils/constants/calendarFilters';

interface OptionType {
  displayName: string;
  id?: number;
  resultStatus?: string;
}

export interface AppliedFilters {
  clinicIds?: string[];
  setShiftBlocks?: string[]; // always send as string[] to API
  clientId?: number | null;
  isUnscheduledSetBlocks?: boolean;
  isPartiallyScheduledSetBlocks?: boolean;
  carePlanstatus?: string[];
  checkedInsuranceCategories?: string[];
  checkedInsurancePayors?: string[];
  checkedInsurancePlans?: string[];
  isWaitlistReason?: string[];
  isJumpstarter?: boolean | null;
  isVBC?: boolean | null;
  recommendedHoursRange?: [number, number];
}

const statusFilterIds = [1, 2];

const transformGraphQLDataToTableData = (
  graphqlData: any[]
): IClientTabRow[] => {
  if (!graphqlData || !Array.isArray(graphqlData)) return [];

  return graphqlData.map(item => {
    const acceptedBlock =
      item.acceptedSchedule?.map(
        (schedule: any) => `${schedule.dayOfWeek}\n${schedule.times}`
      ) || [];

    const scheduledBlock =
      item.scheduledForUI?.map(
        (schedule: any) => `${schedule.dayOfWeek}\n${schedule.times}`
      ) || [];

    // Remaining column shows genuinely-open blocks plus opening-soon blocks.
    // The API returns that combined view under remainingScheduleForUI (the plain
    // remainingSchedule excludes opening-soon so the automation doesn't offer
    // still-covered slots).
    const remainingBlock = (item.remainingScheduleForUI || []).map(
      (schedule: any) => ({
        text: `${schedule.dayOfWeek}\n${schedule.times}`,
        opensOn: schedule.opensOn,
        hasNonBTBooking: schedule.hasNonBTBooking
      })
    );

    return {
      client: item.client?.name || item.clientName || item.name || 'Unknown',
      clinic: item.clinic?.abbreviation
        ? `${item.clinic.name} (${item.clinic.abbreviation})`
        : item.clinic?.name || 'Unknown',
      acceptedBlock: acceptedBlock.length > 0 ? acceptedBlock : ['None'],
      scheduledBlock: scheduledBlock.length > 0 ? scheduledBlock : ['None'],
      remainingBlock: remainingBlock.length > 0 ? remainingBlock : [{ text: 'None' }],
      remainingBlockRaw: item.remainingScheduleRaw,
      link: true,
      clientId: item.client?.id?.toString() || '',
      name: item.client?.name || item.clientName || item.name || 'Unknown',
      waitlistedReason: item.waitlistedReason || 'N/A',
      recommended: item.recommended || 0
    };
  });
};

export default function ClientTab() {
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);
  const [shift, setShift] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState<number | null>(null);
  const [status, setStatus] = useState<number[]>(statusFilterIds);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tableData, setTableData] = useState<IClientTabRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<OptionType[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isClientSearchSelected, setIsClientSearchSelected] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const [filterData, setFilterData] = useState<any>({});
  const [clinicsData, setClinicsData] = useState<IClinicOptionsList[]>([]);
  const [isSearchClicked, setIsSearchClicked] = useState<boolean>(true);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isFirstSearch, setIsFirstSearch] = useState<boolean>(true);
  const [unScheduledClientsCount, setUnScheduledClientsCount] = useState(0);
  const [
    partiallyScheduledClientsCount,
    setPartiallyScheduledClientsCount
  ] = useState(0);

  const statusFilters = [
    { id: 1, label: 'Unscheduled Set Shift', count: unScheduledClientsCount },
    {
      id: 2,
      label: 'Partially Scheduled Set Shift',
      count: partiallyScheduledClientsCount
    }
  ];

  const [doClientRequest, { data: clientLists }] = useLazyQuery(GET_ATTENDEES, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const nonDisplayedAttendees = clientLists?.attendees?.sort(
        (a1: OptionType, a2: OptionType) =>
          alphabeticalSort(a1.displayName, a2.displayName)
      );
      if (nonDisplayedAttendees && nonDisplayedAttendees.length > 0) {
        setSearchSuggestions(nonDisplayedAttendees);
      } else {
        setSearchSuggestions([
          { displayName: 'No BTs found', resultStatus: '__NO_RESULTS__' }
        ]);
      }
      setIsSearchLoading(false);
    }
  });

  const { loading: isLoading } = useQuery(GET_WAITLIST_INITIAL_LOAD_DATA, {
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      let {
        clinicOptionsList,
        waitListStatus,
        waitListReasons,
        insuranceCategories,
        insurancePayors,
        insurancePlans
      } = data.getWaitListIntialLoadData.filterList;

      setClinicsData(clinicOptionsList);

      setFilterData({
        waitListStatus,
        waitListReasons,
        insuranceCategories,
        insurancePayors,
        insurancePlans
      });
    },
    onError: error => {
      console.log(error, 'Error while getting location');
    }
  });

  const {
    data: shiftDropdownOptions,
    loading: shiftDropdownLoading
  } = useQuery(GET_SHIFT_DROPDOWN_OPTIONS);
  const shiftOptions = (
    shiftDropdownOptions?.getShiftDropdownOptions || []
  ).map(
    (shift: {
      shiftId: number;
      description: string;
      blocks: { id: number }[];
    }) => ({
      label: shift.description,
      value: shift.shiftId,
      blockIds: shift.blocks.map(block => {
        return block.id;
      })
    })
  );

  const [fetchCareConnectClients] = useLazyQuery(GET_CARECONNECT_CLIENTS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const rawSchedules =
        data?.getClientScheduleSummaries?.clientSchedules || [];
      const schedulesWithRemaining = rawSchedules.filter(
        (schedule: any) =>
          Array.isArray(schedule.remainingSchedule) &&
          schedule.remainingSchedule.length > 0
      );
      const removedCount = rawSchedules.length - schedulesWithRemaining.length;
      const transformedData = transformGraphQLDataToTableData(
        schedulesWithRemaining
      );
      setTableData(transformedData);
      setTotalCount(
        Math.max(
          0,
          (data?.getClientScheduleSummaries?.count || 0) - removedCount
        )
      );
      setUnScheduledClientsCount(
        data?.getClientScheduleSummaries?.unScheduledClientsCount || 0
      );
      setPartiallyScheduledClientsCount(
        data?.getClientScheduleSummaries?.partiallyScheduledClientsCount || 0
      );
      setTableLoading(false);
      setIsSearchClicked(false);
    },
    onError: error => {
      console.error('GraphQL query error:', error);
      setTableLoading(false);
      setIsSearchClicked(false);
    }
  });

  const clinicsMap = useMemo(() => {
    const map = new Map();
    (clinicsData as IClinicOptionsList[])?.forEach(item => {
      map.set(item.id, {
        id: item.id,
        name: item.name,
        abbreviation: item.abbreviation,
        state: item.state
      });
    });
    return map;
  }, [clinicsData]);

  const clinicOptions = useMemo(
    () =>
      Array.from(clinicsMap?.values() || []).map(c => ({
        label:
          c.abbreviation && c.abbreviation !== '' && c.abbreviation !== null
            ? c.name + ' (' + c.abbreviation + ')'
            : c.name,
        value: c.id
      })),
    [clinicsMap]
  );

  const handleShowAdditionalFilters = useCallback((show: boolean) => {
    setShowAdditionalFilters(show);
  }, []);

  const fetchClientData = useCallback(
    async (
      sidebarFilters?: AppliedFilters,
      pageNumber?: number,
      pageRowSize?: number,
      isUnscheduledSetBlocks?: boolean,
      isPartiallyScheduledSetBlocks?: boolean
    ) => {
      const selectedBlockIds = shift.flatMap(shiftId => {
        const selectedShift = shiftOptions.find(
          (option: { value: number; blockIds: number[] }) =>
            option.value === parseInt(shiftId)
        );
        return selectedShift?.blockIds || [];
      });

      const currentFilters = {
        clinicIds: selectedClinics,
        setShiftBlocks: selectedBlockIds.map(id => id),
        clientId: clientId,
        isUnscheduledSetBlocks: isUnscheduledSetBlocks,
        isPartiallyScheduledSetBlocks: isPartiallyScheduledSetBlocks,
        page: pageNumber,
        pageSize: pageRowSize,
        carePlanstatus: sidebarFilters?.carePlanstatus,
        checkedInsuranceCategories: sidebarFilters?.checkedInsuranceCategories,
        checkedInsurancePayors: sidebarFilters?.checkedInsurancePayors,
        checkedInsurancePlans: sidebarFilters?.checkedInsurancePlans,
        isWaitlistReason: sidebarFilters?.isWaitlistReason,
        isVBC: sidebarFilters?.isVBC,
        isJumpstarter: sidebarFilters?.isJumpstarter,
        ...(sidebarFilters?.recommendedHoursRange &&
          (sidebarFilters?.recommendedHoursRange[0] !==
            RECOMMENDED_HOURS_RANGE[0] ||
            sidebarFilters?.recommendedHoursRange[1] !==
              RECOMMENDED_HOURS_RANGE[1]) && {
            recommendedHoursRange: sidebarFilters?.recommendedHoursRange
          })
      };

      setTableLoading(true);
      fetchCareConnectClients({
        variables: {
          input: currentFilters
        }
      });
    },
    [shift, shiftOptions, selectedClinics, clientId, fetchCareConnectClients]
  );

  const handleStatusChange = useCallback(
    (id: number) => {
      let newStatus = status;
      if (newStatus.includes(id)) {
        newStatus = newStatus.filter(i => i !== id);
      } else {
        newStatus = [...newStatus, id];
      }
      setStatus(newStatus);
      fetchClientData(
        appliedFilters,
        0,
        rowsPerPage,
        !newStatus.includes(statusFilterIds[0]),
        !newStatus.includes(statusFilterIds[1])
      );
    },
    [appliedFilters, rowsPerPage, fetchClientData]
  );

  const triggerSearch = useCallback(() => {
    const selectedBlockIds = shift.flatMap(shiftId => {
      const selectedShift = shiftOptions.find(
        (option: { value: number; blockIds: number[] }) =>
          option.value === parseInt(shiftId)
      );
      return selectedShift?.blockIds || [];
    });

    const currentFilters = {
      ...appliedFilters,
      clinicIds: selectedClinics,
      setShiftBlocks: selectedBlockIds.map(id => id),
      clientId: clientId
    };

    setAppliedFilters(currentFilters);

    setPage(0);
    setTotalCount(0);
    setHasSearched(true);
    setUnScheduledClientsCount(0);
    setPartiallyScheduledClientsCount(0);

    if (isFirstSearch) {
      fetchClientData(currentFilters, 0, rowsPerPage, true, true);
      setIsFirstSearch(false);
    } else {
      const isDefaultState = statusFilterIds.every(id => status.includes(id));
      if (isDefaultState) {
        fetchClientData(currentFilters, 0, rowsPerPage, true, true);
      } else {
        fetchClientData(
          currentFilters,
          0,
          rowsPerPage,
          !status.includes(statusFilterIds[0]),
          !status.includes(statusFilterIds[1])
        );
      }
    }
  }, [
    selectedClinics,
    shift,
    clientId,
    shiftOptions,
    status,
    rowsPerPage,
    isFirstSearch,
    fetchClientData,
    appliedFilters
  ]);

  useEffect(() => {
    const hasAnyFilter =
      selectedClinics.length > 0 || shift.length > 0 || clientId !== null;
    if (hasAnyFilter) {
      triggerSearch();
    } else {
      setTableData([]);
      setTableLoading(false);
      setHasSearched(false);
    }
  }, [selectedClinics, shift, clientId, status]);

  const handleAdditionalFiltersChange = useCallback(
    async (filters: Partial<AppliedFilters>) => {
      const selectedBlockIds = shift.flatMap(shiftId => {
        const selectedShift = shiftOptions.find(
          (option: { value: number; blockIds: number[] }) =>
            option.value === parseInt(shiftId)
        );
        return selectedShift?.blockIds || [];
      });

      const sidebarFilters = {
        clinicIds: selectedClinics,
        setShiftBlocks: selectedBlockIds.map(id => id),
        clientId: search ? clientId : null,
        ...filters
      };

      setAppliedFilters(prev => {
        const newFilters = {
          ...prev,
          clinicIds: selectedClinics,
          setShiftBlocks: selectedBlockIds.map(id => id),
          clientId: search ? prev.clientId : null,
          ...filters
        };
        return newFilters;
      });
      setPage(0);

      const hasPrimaryFilters =
        selectedClinics.length > 0 || shift.length > 0 || clientId !== null;
      const hasAdditionalFilters =
        (sidebarFilters.carePlanstatus &&
          sidebarFilters.carePlanstatus.length > 0) ||
        (sidebarFilters.checkedInsuranceCategories &&
          sidebarFilters.checkedInsuranceCategories.length > 0) ||
        (sidebarFilters.checkedInsurancePayors &&
          sidebarFilters.checkedInsurancePayors.length > 0) ||
        (sidebarFilters.checkedInsurancePlans &&
          sidebarFilters.checkedInsurancePlans.length > 0) ||
        (sidebarFilters.isWaitlistReason &&
          sidebarFilters.isWaitlistReason.length > 0) ||
        (sidebarFilters.isJumpstarter !== null &&
          sidebarFilters.isJumpstarter !== undefined) ||
        (sidebarFilters.isVBC !== null && sidebarFilters.isVBC !== undefined) ||
        (sidebarFilters.recommendedHoursRange &&
          (sidebarFilters.recommendedHoursRange[0] !==
            RECOMMENDED_HOURS_RANGE[0] ||
            sidebarFilters.recommendedHoursRange[1] !==
              RECOMMENDED_HOURS_RANGE[1]));

      if (hasPrimaryFilters || hasAdditionalFilters) {
        await fetchClientData(
          sidebarFilters,
          0,
          rowsPerPage,
          !status.includes(statusFilterIds[0]),
          !status.includes(statusFilterIds[1])
        );
      } else {
        setTableData([]);
        setTableLoading(false);
        setHasSearched(false);
      }
    },
    [
      selectedClinics,
      shift,
      shiftOptions,
      search,
      clientId,
      status,
      rowsPerPage,
      fetchClientData
    ]
  );
  const handleClinicApply = useCallback((selected: string[]) => {
    setSelectedClinics(selected);
  }, []);

  const handleShiftApply = useCallback((selected: string[]) => {
    setShift(selected);
  }, []);

  const handleClientChange = useCallback(
    (_: any, value: OptionType | string | null | number) => {
      if (
        value &&
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        value.resultStatus !== '__NO_RESULTS__'
      ) {
        setSearch(value.displayName);
        setClientId(value.id || null);
        setIsClientSearchSelected(true);
      } else {
        setIsClientSearchSelected(false);
        setClientId(null);
      }
    },
    []
  );

  const handleClientInputChange = useCallback(
    (_: any, value: string, reason: string) => {
      if (reason === 'input') {
        setSearch(value);
        setIsClientSearchSelected(false);
        if (value.length >= 3) {
          setIsSearchLoading(true);
          setSearchOpen(true);
          doClientRequest({
            variables: {
              name: value?.trim(),
              isClient: true,
              shouldExcludeActive: true
            }
          });
        } else {
          setSearchSuggestions([]);
          setSearchOpen(false);
        }
      }
    },
    [doClientRequest]
  );

  const handleClearClientSearch = useCallback(() => {
    setSearch('');
    setClientId(null);
    setIsClientSearchSelected(false);
    setSearchSuggestions([]);
    setSearchOpen(false);
  }, []);

  const handleRemoveFilter = useCallback(
    (filterType: string, value?: string) => {
      setAppliedFilters(prev => {
        const newFilters = { ...prev };

        switch (filterType) {
          case 'carePlanstatus':
            if (value) {
              newFilters.carePlanstatus =
                prev.carePlanstatus?.filter(v => v !== value) || [];
            } else {
              newFilters.carePlanstatus = [];
            }
            break;
          case 'isWaitlistReason':
            if (value) {
              newFilters.isWaitlistReason =
                prev.isWaitlistReason?.filter(v => v !== value) || [];
            } else {
              newFilters.isWaitlistReason = [];
            }
            break;
          case 'isJumpstarter':
            newFilters.isJumpstarter = null;
            break;
          case 'isVBC':
            newFilters.isVBC = null;
            newFilters.checkedInsuranceCategories = [];
            newFilters.checkedInsurancePayors = [];
            newFilters.checkedInsurancePlans = [];
            break;
          case 'checkedInsuranceCategories':
            if (value) {
              newFilters.checkedInsuranceCategories =
                prev.checkedInsuranceCategories?.filter(v => v !== value) || [];
            } else {
              newFilters.checkedInsuranceCategories = [];
            }
            break;
          case 'checkedInsurancePayors':
            if (value) {
              newFilters.checkedInsurancePayors =
                prev.checkedInsurancePayors?.filter(v => v !== value) || [];
            } else {
              newFilters.checkedInsurancePayors = [];
            }
            break;
          case 'checkedInsurancePlans':
            if (value) {
              newFilters.checkedInsurancePlans =
                prev.checkedInsurancePlans?.filter(v => v !== value) || [];
            } else {
              newFilters.checkedInsurancePlans = [];
            }
            break;
          case 'recommendedHoursRange':
            newFilters.recommendedHoursRange = RECOMMENDED_HOURS_RANGE;
            break;
        }

        if (hasSearched) {
          const hasPrimaryFilters =
            selectedClinics.length > 0 || shift.length > 0 || clientId !== null;
          const hasAdditionalFilters =
            (newFilters.carePlanstatus &&
              newFilters.carePlanstatus.length > 0) ||
            (newFilters.checkedInsuranceCategories &&
              newFilters.checkedInsuranceCategories.length > 0) ||
            (newFilters.checkedInsurancePayors &&
              newFilters.checkedInsurancePayors.length > 0) ||
            (newFilters.checkedInsurancePlans &&
              newFilters.checkedInsurancePlans.length > 0) ||
            (newFilters.isWaitlistReason &&
              newFilters.isWaitlistReason.length > 0) ||
            (newFilters.isJumpstarter !== null &&
              newFilters.isJumpstarter !== undefined) ||
            (newFilters.isVBC !== null && newFilters.isVBC !== undefined) ||
            (newFilters.recommendedHoursRange &&
              (newFilters.recommendedHoursRange[0] !==
                RECOMMENDED_HOURS_RANGE[0] ||
                newFilters.recommendedHoursRange[1] !==
                  RECOMMENDED_HOURS_RANGE[1]));

          if (hasPrimaryFilters || hasAdditionalFilters) {
            setTimeout(() => {
              fetchClientData(
                newFilters,
                0,
                rowsPerPage,
                !status.includes(statusFilterIds[0]),
                !status.includes(statusFilterIds[1])
              );
            }, 0);
          } else {
            // No filters remain - clear the table data
            setTableData([]);
            setTableLoading(false);
            setHasSearched(false);
          }
        }

        return newFilters;
      });
    },
    [
      hasSearched,
      fetchClientData,
      rowsPerPage,
      status,
      selectedClinics,
      shift,
      clientId
    ]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (!hasSearched) return;
      setPage(newPage);
      fetchClientData(
        appliedFilters,
        newPage,
        rowsPerPage,
        !status.includes(1),
        !status.includes(2)
      );
    },
    [hasSearched, fetchClientData, appliedFilters, status]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setRowsPerPage(newPageSize);
      setPage(0);
      if (hasSearched) {
        fetchClientData(
          appliedFilters,
          0,
          newPageSize,
          !status.includes(statusFilterIds[0]),
          !status.includes(statusFilterIds[1])
        );
      }
    },
    [hasSearched, fetchClientData, appliedFilters, status]
  );

  const handleRowSelect = useCallback(() => {}, []);

  const getRowKey = useCallback(
    (record: any) => record.clientId || record.bt,
    []
  );

  const getRowClassName = useCallback(() => 'custom-client-tab-row', []);

  const columns = useMemo(() => CURRENT_CLIENT_TAB_COLUMNS(), []);

  const anyFilterSelected =
    selectedClinics.length > 0 ||
    shift.length > 0 ||
    clientId !== null ||
    (appliedFilters.carePlanstatus &&
      appliedFilters.carePlanstatus.length > 0) ||
    (appliedFilters.checkedInsuranceCategories &&
      appliedFilters.checkedInsuranceCategories.length > 0) ||
    (appliedFilters.checkedInsurancePayors &&
      appliedFilters.checkedInsurancePayors.length > 0) ||
    (appliedFilters.checkedInsurancePlans &&
      appliedFilters.checkedInsurancePlans.length > 0) ||
    (appliedFilters.isWaitlistReason &&
      appliedFilters.isWaitlistReason.length > 0) ||
    (appliedFilters.isJumpstarter !== null &&
      appliedFilters.isJumpstarter !== undefined) ||
    (appliedFilters.isVBC !== null && appliedFilters.isVBC !== undefined) ||
    (appliedFilters.recommendedHoursRange &&
      (appliedFilters.recommendedHoursRange[0] !== RECOMMENDED_HOURS_RANGE[0] ||
        appliedFilters.recommendedHoursRange[1] !==
          RECOMMENDED_HOURS_RANGE[1]));

  console.log('appliedFilters', appliedFilters);

  return (
    <Box style={{ backgroundColor: '#FCFCFC', padding: 20 }}>
      <Grid container spacing={2} className="filterBar" alignItems="center">
        <Grid item xs={8} sm={8} md={4} lg={3}>
          <Box position="relative">
            <MultiSelectDropdown
              label="Clinic"
              options={clinicOptions}
              selected={selectedClinics}
              onApply={handleClinicApply}
              placeholder={isLoading ? 'Loading clinics...' : 'Clinic'}
              disabled={isLoading}
            />
            {isLoading && (
              <Box
                position="absolute"
                top="50%"
                right={40}
                style={{ transform: 'translateY(-50%)', zIndex: 1 }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={8} sm={8} md={4} lg={3}>
          <Box position="relative">
            <MultiSelectDropdown
              label="Remaining Shift"
              options={shiftOptions}
              selected={shift}
              onApply={handleShiftApply}
              placeholder="Remaining Shift"
              disabled={shiftDropdownLoading}
            />
            {shiftDropdownLoading && (
              <Box
                position="absolute"
                top="50%"
                right={40}
                style={{ transform: 'translateY(-50%)', zIndex: 1 }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={8} sm={8} md={4} lg={3}>
          <AutocompleteSearch
            isClient={true}
            placeholder="Client"
            search={search}
            isSearchLoading={isSearchLoading}
            searchSuggestions={searchSuggestions}
            searchOpen={searchOpen}
            isSearchSelected={isClientSearchSelected}
            onInputChange={handleClientInputChange}
            onChange={handleClientChange}
            onClear={handleClearClientSearch}
            onOpen={() => {
              if (search.length >= 3) setSearchOpen(true);
            }}
            onClose={() => setSearchOpen(false)}
          />
        </Grid>
        <Grid
          item
          xs={8}
          sm={8}
          md={4}
          lg={2}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Typography
            style={{
              cursor: 'pointer',
              color: '#6f42f5',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 500
            }}
            onClick={() => handleShowAdditionalFilters(true)}
          >
            <FilterListIcon style={{ marginRight: '8px', fill: '#6f42f5' }} />{' '}
            Additional Filters
          </Typography>
        </Grid>
      </Grid>

      {/* Display applied filter chips */}
      <FilterChips
        appliedFilters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
      />

      <Box display="flex" alignItems="center" mt={2}>
        <Box className="ClientTabStatusBar">
          {statusFilters.map((f, idx) => (
            <Button
              key={f.label}
              className={
                anyFilterSelected
                  ? isSearchClicked
                    ? 'ClientTabStatusButtonDisabled'
                    : statusFilterIds.every(v => status.includes(v))
                    ? 'ClientTabStatusButtonInactive'
                    : !status.includes(f.id)
                    ? 'ClientTabStatusButton'
                    : 'ClientTabStatusButtonInactive'
                  : 'ClientTabStatusButtonDisabled'
              }
              style={{
                border: anyFilterSelected
                  ? isSearchClicked
                    ? '1px solid #e0e0e0'
                    : '1px solid #6f42f5'
                  : '1px solid #e0e0e0',
                borderRight:
                  idx === statusFilters.length - 1 ? 'none' : '1px solid #000',
                borderRadius:
                  idx === 0
                    ? '4px 0 0 4px'
                    : idx === statusFilters.length - 1
                    ? '0 4px 4px 0'
                    : '0'
              }}
              onClick={() => handleStatusChange(f.id)}
              disabled={isSearchClicked}
            >
              {f.label} ({f.count})
            </Button>
          ))}
        </Box>
      </Box>
      <Paper style={{ marginBottom: 16, marginTop: 20 }}>
        <DataGrid
          columns={columns}
          data={tableData}
          total={totalCount}
          page={page + 1}
          pageSize={rowsPerPage}
          loading={tableLoading}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onRowSelect={handleRowSelect}
          rowKey={getRowKey}
          style={{ minWidth: '1200px' }}
          rowClassName={getRowClassName}
          emptyText={
            !anyFilterSelected || !hasSearched
              ? 'Select atleast one filter to show results.'
              : 'No data available'
          }
        />
      </Paper>

      <AdditionalFilters
        show={showAdditionalFilters}
        close={() => handleShowAdditionalFilters(false)}
        onFiltersChange={handleAdditionalFiltersChange}
        filterData={filterData}
        isLoading={isLoading}
        setHasSearched={setHasSearched}
        appliedFilters={appliedFilters}
        disabledClearFilters={!anyFilterSelected}
      />
    </Box>
  );
}
